import assign from 'lodash/assign';
import chunk from 'lodash/chunk';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import pullAt from 'lodash/pullAt';
import reject from 'lodash/reject';

import { getCache } from 'Apollo';
import StateManager from 'Apollo/StateManager';
import {
	ShowroomParams,
	ShowroomPrContactParams,
	SectionFileUi,
	SectionRowUi,
	GetLibrarySelectedMediasEditorQuery,
	Image,
	Video,
	ShowroomSectionUi,
	ShowroomSectionOptionsUi,
	Media,
	ShowroomEditor,
	MediaType
} from 'generated/graphql';

import { AddMediaToSectionParams, SectionData } from 'Types/showroom';

import { SELECTED_LIBRARY_MEDIAS_EDITOR } from './queries';
import INITIAL_STATE from './state';

const path = 'showroom.editor';
const stateManager = new StateManager<ShowroomEditor>(path);
const cache = getCache();

export type EditorDragAndDropCoordinates = {
	fromRow: number;
	row: number;
	fromColumn: number;
	column: number;
};

export const openEditorMode = (showroomId?: string) => {
	stateManager.update({
		isOpen: true,
		showroomId: showroomId ? showroomId : null,
		step: 0
	});
};

export const closeEditorMode = () => {
	stateManager.update({
		...INITIAL_STATE
	});
};

export const goToStep = (step: number) => {
	stateManager.update({
		step
	});
};

export const goToSection = (section: number) => {
	stateManager.update({
		displaySectionOptions: null,
		currentSection: section
	});
};

export const setDisplaySectionOptions = (type: string) => {
	const selected = stateManager.get().displaySectionOptions;
	stateManager.update({
		displaySectionOptions: selected === type ? null : type
	});
};

export const updateShowroomParams = (showroomParams: Partial<ShowroomParams>) => {
	stateManager.update({
		showroomParams: assign({}, stateManager.get().showroomParams, showroomParams)
	});
};

export const updateShowroomMediaParams = (sectionId: string, medias: Partial<Media>[]) => {
	const { showroomParams }: ShowroomEditor = stateManager.get();
	const sections = cloneDeep(showroomParams.sections);
	const section: ShowroomSectionUi = sections.find(section => section.id === sectionId);
	medias.forEach(media => {
		const rowIndex = findIndex(section.rowMediaIds, row => row.mediaIds.includes(media.id));
		const row = section.rows[rowIndex * 2 + 1];
		const sortedRow = section.rowMediaIds[rowIndex];
		const mediaIndex = findIndex(sortedRow.mediaIds, rowMediaId => rowMediaId === media.id);

		row.medias.splice(mediaIndex, 0, {
			mediaId: media.id,
			publicId: media.cloudinaryId,
			mediaType: media.mediaType,
			width: media.mediaType === MediaType.Image ? (media as Image).width : media.mediaType === MediaType.Video ? (media as Video).width : 0,
			height: media.mediaType === MediaType.Image ? (media as Image).height : media.mediaType === MediaType.Video ? (media as Video).height : 0,
			originalFilename: media.name || '',
			createdAt: media.createdAt || 0,
			cloudinaryVersion: media.cloudinaryVersion?.toString(),
			isSample: media.isSample
		});
	});
	stateManager.update({
		showroomParams: { ...showroomParams, sections }
	});
};

export const updateShowroomSectionLoading = (sectionId: string, loading: boolean) => {
	const { showroomParams } = stateManager.get();
	const sections = cloneDeep(showroomParams.sections);
	const section: ShowroomSectionUi = sections?.find(section => section.id === sectionId);
	if (section) {
		section.loading = loading;
		stateManager.update({
			showroomParams: { ...showroomParams, sections }
		});
	}
};

export const updateShowroomSectionLoaded = (sectionId: string, loaded: boolean) => {
	const { showroomParams } = stateManager.get();
	const sections = cloneDeep(showroomParams.sections);
	const section: ShowroomSectionUi = sections?.find(section => section.id === sectionId);
	if (section) {
		section.loaded = loaded;
		stateManager.update({
			showroomParams: { ...showroomParams, sections }
		});
	}
};

export const updateSectionOptions = (section: number, options: Partial<ShowroomSectionOptionsUi>) => {
	const showroomParams = cloneDeep(stateManager.get().showroomParams);
	showroomParams.sections[section].options = assign({}, showroomParams.sections[section].options, options);
	stateManager.update({
		showroomParams
	});
};

const getNewMediasToAdd = (section: ShowroomSectionUi): SectionFileUi[] => {
	const { librarySelectedMedias: selectedMedias } = cache.readQuery<GetLibrarySelectedMediasEditorQuery>({ query: SELECTED_LIBRARY_MEDIAS_EDITOR });
	const sectionFilesIds = map(flatten(map(section.rows, 'medias')), 'mediaId').reduce((acc, mediaId) => {
		acc[mediaId] = mediaId;
		return acc;
	}, {});

	return selectedMedias.reduce((acc: SectionFileUi[], media: Image | Video) => {
		if (!sectionFilesIds[media.id])
			acc.push({
				mediaId: media.id,
				mediaType: media.mediaType,
				publicId: media.cloudinaryId,
				cloudinaryVersion: media.cloudinaryVersion.toString(),
				width: media.width,
				height: media.height,
				originalFilename: media.name,
				createdAt: media.createdAt,
				isSample: media.isSample
			});
		return acc;
	}, []);
};

/**
 * This function takes care of the empty rows after moving images
 * - If the row should be empty but it's not, we add an extra empty row
 * - If the row is empty and it shouldn't we remove the empty one.
 * @param currentSection The section to act
 * @param emptyRow An empty row used to generate new emptyRows
 * @param row The affected row where the algorithm should apply
 */
const fixRows = (currentSection: ShowroomSectionUi, emptyRow: SectionRowUi, row: number) => {
	let startFixFrom;
	let endFixFrom;

	const maxRows = currentSection.rows.length - 1;

	if (row === 0) {
		startFixFrom = row;
		endFixFrom = row + 2;
	} else if (row === maxRows) {
		startFixFrom = row - 2;
		endFixFrom = row;
	} else {
		startFixFrom = row - 3 <= 0 ? 0 : row - 3;
		endFixFrom = row + 3 >= maxRows ? maxRows : row + 3;
	}

	while (startFixFrom <= endFixFrom && startFixFrom !== currentSection.rows.length) {
		const shouldBeEmpty = startFixFrom % 2 === 0;

		if (shouldBeEmpty && !isEmpty(currentSection.rows[startFixFrom].medias)) {
			currentSection.rows.splice(startFixFrom, 0, cloneDeep(emptyRow));
			startFixFrom++;
		} else {
			if (!shouldBeEmpty && isEmpty(currentSection.rows[startFixFrom].medias)) {
				currentSection.rows.splice(startFixFrom, 1);
			} else {
				startFixFrom++;
			}
		}
	}
};

const readSectionData = (section: number): SectionData => {
	const {
		showroomParams: { sections }
	}: ShowroomEditor = stateManager.get();
	return cloneDeep(sections[section]);
};

const updateSection = (section: number, sectionData: SectionData) => {
	const {
		showroomParams: { sections }
	}: ShowroomEditor = stateManager.get();
	const sectionsCopy = [...sections];
	sectionsCopy[section] = sectionData;
	updateShowroomParams({ sections: sectionsCopy });
};

export const reorganizeSection = (section: number, itemsPerRow: number, sortBy: string) => {
	const currentSection = readSectionData(section);
	const medias = flatten(currentSection.rows.map(row => row.medias));
	currentSection.rows = [];
	const updatedSection = addMediasToSection({ newMedias: medias, section: currentSection, itemsPerRow, sortBy, rowIndex: 0 });
	updateSection(section, updatedSection);
};

export const addSelectedMediasToSection = (section: number, itemsPerRow: number, sortBy: string, rowIndex: number) => {
	const currentSection = readSectionData(section);
	const newMedias: SectionFileUi[] = getNewMediasToAdd(currentSection);
	const updatedSection = addMediasToSection({ newMedias, section: currentSection, itemsPerRow, sortBy, rowIndex });
	updateSection(section, updatedSection);
};

export const addMediasToSection = ({ newMedias, section, itemsPerRow, sortBy, rowIndex }: AddMediaToSectionParams) => {
	const currentSection = cloneDeep(section);
	const [field, order] = sortBy.split(':');
	const sortedArray = newMedias.sort((a, b) => {
		const fieldA = field === 'originalFilename' ? a.originalFilename.toLowerCase() : a[field];
		const fieldB = field === 'originalFilename' ? b.originalFilename.toLowerCase() : b[field];
		return fieldA.toString().localeCompare(fieldB.toString(), undefined, { numeric: true, sensitivity: 'base' });
	});

	if (order === 'desc') sortedArray.reverse();

	const splittedItems = chunk(sortedArray, itemsPerRow);

	const newRows: SectionRowUi[] = [];
	splittedItems.forEach(items => {
		newRows.push({
			medias: []
		});
		newRows.push({
			medias: items
		});
	});

	const firstRows = currentSection.rows.slice(0, rowIndex) || [];
	const lastRows = currentSection.rows.slice(rowIndex) || [];

	if (isEmpty(lastRows))
		newRows.push({
			medias: []
		});

	currentSection.rows = [...firstRows, ...newRows, ...lastRows];

	const emptyRow = currentSection.rows.find(row => isEmpty(row.medias));
	fixRows(currentSection, emptyRow, rowIndex);

	return currentSection;
};

export const addMediaToRow = (section: number, row: number, start: number) => {
	const currentSection = readSectionData(section);

	const newMedias: SectionFileUi[] = getNewMediasToAdd(currentSection);
	const currentRow = currentSection.rows[row];
	const firstFiles = currentRow.medias?.slice(0, start) || [];
	const lastFiles = currentRow.medias?.slice(start) || [];

	currentRow.medias = [...firstFiles, ...newMedias, ...lastFiles];
	updateSection(section, currentSection);
};

export const removeMediaFromSection = (section: number, row: number, mediaId: string) => {
	const currentSection = readSectionData(section);
	const currentRow = currentSection.rows[row];
	currentRow.medias = reject(currentRow.medias, { publicId: mediaId });
	if (isEmpty(currentRow.medias)) pullAt(currentSection.rows, [row, row + 1]);
	updateSection(section, currentSection);
};

export const updatePrContactValues = (values: Partial<ShowroomPrContactParams>, index: number) => {
	const prContacts = [...stateManager.get().showroomParams.prContacts];
	const prContact = { ...prContacts[index], ...values };
	prContacts[index] = prContact;
	updateShowroomParams({ prContacts });
};

export const addEmptyContact = () => {
	const prContacts = [...stateManager.get().showroomParams.prContacts];
	prContacts.push({
		name: '',
		email: '',
		phone: '',
		jobTitle: ''
	});
	updateShowroomParams({ prContacts });
};

export const deleteContact = (index: number) => {
	const prContacts = [...stateManager.get().showroomParams.prContacts];
	prContacts.splice(index, 1);
	updateShowroomParams({ prContacts });
};

export const moveMediaToRow = (section: number, coordinates: EditorDragAndDropCoordinates) => {
	const currentSection = readSectionData(section);

	const { fromRow, fromColumn, row, column } = coordinates;

	const originRow = currentSection.rows[fromRow];
	const destinationRow = currentSection.rows[row];

	const splicedItems = originRow.medias.splice(fromColumn, 1);

	if (fromRow === row && column / 2 > fromColumn) {
		destinationRow.medias.splice(column / 2 - 1, 0, splicedItems[0]);
	} else {
		destinationRow.medias.splice(column / 2, 0, splicedItems[0]);
	}

	const emptyRow = currentSection.rows.find(row => row.medias.length === 0);

	fixRows(currentSection, emptyRow, fromRow);
	fixRows(currentSection, emptyRow, row);

	if (!isEmpty(currentSection.rows[currentSection.rows.length - 1].medias)) {
		currentSection.rows = [...currentSection.rows, cloneDeep(emptyRow)];
	}

	updateSection(section, currentSection);
	return currentSection;
};

export const getCleanedShowroomParams = (): Partial<ShowroomParams> => {
	const { showroomParams } = stateManager.get();

	const showroomParamsCopy = cloneDeep(showroomParams);

	showroomParamsCopy.sections.forEach(section => {
		section.rows = filter(section.rows, row => !isEmpty(row.medias));
	});

	showroomParamsCopy.prContacts = showroomParamsCopy.prContacts.filter(prContact => !Object.values(prContact).every(value => !value));

	return showroomParamsCopy;
};
